/**
 * @file MoveFormulationDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-08-25
 * @description Move one formulation to a customer, from the row itself.
 *
 *              The organiser is for working through a backlog. This is for the
 *              single case you notice while reading the table — a blend filed
 *              under the wrong farm, or under none — and it should not cost you
 *              a trip through a bulk tool to fix.
 *
 *              The destination customer need not already have blend lines: the
 *              endpoint creates the line if it is new, so "this belongs to a
 *              farm that has never had a blend before" is an ordinary move
 *              rather than a dead end.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import CustomerBlendPicker, { useBlendAccounts } from './CustomerBlendPicker';
import CustomerProductsModal from './CustomerProductsModal';
import useCustomerBlends from '../hooks/useCustomerBlends';
import { useSlideOutButton } from './SlideOutButton';
import './CustomerBlendPicker.css';

function MoveBody({ row, target, setTarget, accounts, blends, onViewCustomer, busy, error }) {
  useSlideOutButton([busy]);
  return (
    <div className="mfd-body">
      <p className="mfd-what">
        <span className="account-badge-inline">{row.item_code}</span>
        <span className="mfd-name">{row.item_name}</span>
      </p>
      <p className="mfd-hint">
        Where this formulation belongs. A customer with no blend lines yet is
        fine — name the line and it is created.
      </p>

      {accounts.loading ? (
        <LoadingSpinner />
      ) : (
        <CustomerBlendPicker
          accountOptions={accounts.options}
          accountsById={accounts.byId}
          blends={blends}
          value={target}
          onChange={setTarget}
          onViewCustomer={onViewCustomer}
          disabled={busy}
        />
      )}

      {(error || accounts.error) && <p className="cbo-error">{error || accounts.error}</p>}
    </div>
  );
}

function MoveFormulationDialog({ row, onClose, onMoved }) {
  const accounts = useBlendAccounts();
  const { blends } = useCustomerBlends();
  const [target, setTarget] = useState({
    accountId: row.customer_blend_account_id ? String(row.customer_blend_account_id) : null,
    name: row.customer_blend_name || ''
  });
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canSave = !!target.accountId && !!String(target.name || '').trim() && !busy;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/formulations/customer-blends/place/${row.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ account_id: Number(target.accountId), name: target.name })
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Move failed');
      onMoved();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={busy ? () => {} : onClose}
        title="Move to customer"
        className="move-formulation"
        footer={
          <div className="buttons slideout">
            <div>
              <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={save} disabled={!canSave}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">move_group</span></div>
                <span className="btn-label">{busy ? 'Moving...' : 'Move'}</span>
              </button>
            </div>
          </div>
        }
      >
        <MoveBody
          row={row}
          target={target}
          setTarget={setTarget}
          accounts={accounts}
          blends={blends}
          onViewCustomer={setViewing}
          busy={busy}
          error={error}
        />
      </Modal>

      {viewing && (
        <CustomerProductsModal account={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

export default MoveFormulationDialog;
